import { Fragment, useEffect, useState, memo, useCallback, useRef } from "react";
import { useAppContext } from "@/components/AppContext";
import { Button, Col, DatePicker, Drawer, Form, Row, Spin, Typography, message, Modal, Select, Tag, Checkbox, Switch, Input, Card, InputNumber, Divider } from "antd";
import { useForm } from 'antd/lib/form/Form';
import { YODA_CDN_URL, PUJA_PRESIGN_URL } from '@/utils/constants';
import { LoadingOutlined, RightOutlined, MinusCircleOutlined, PlusOutlined, MenuOutlined } from "@ant-design/icons";
import PujaMedia from "../PujaMedia";
import MessageCard from "../MessageCard";
import { PujaUpdateForm as PujaUpdateFormType, parseStoreResponse, preparePayload } from "../../utils/helper";
import { ScheduledStates } from "../../types";
import { getMonetisationPujaApi } from "@/utils/helper";
import EmptyState from "@/components/ui/EmptyState";
import useFetch from "@/hooks/useFetch";
import moment from "moment-timezone";
import { showErrorToast } from '@/utils/helper';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;

interface PujaUpdateFormProps {
    showForm: boolean;
    store: { id: string, title: string };
    handleCloseForm: (updateStoreList?: boolean) => void;
}

// Drag handle for sortable items
const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

// Temple Card Component: Does not handle remove action.
const TempleCard = ({ templeField, form }: { templeField: any, form: any }) => {
    const isMultiTemple = form.getFieldValue('multi_temple');
    return (
        <Card
            title={
                <Row justify="space-between" align="middle">
                    <Col>{isMultiTemple ? `Temple ${templeField.name + 1}` : 'Temple Details'}</Col>
                    <Col style={{ marginRight: '30px' }}>{isMultiTemple && <DragHandle />}</Col>
                </Row>
            }
        >
            <div style={{ display: 'none' }}>
                <Form.Item label="Temple id" name={[templeField.name, 'temple_id']} >
                    <Input placeholder="Temple id" />
                </Form.Item>
            </div>
            <Form.Item label="Temple Name" name={[templeField.name, 'temple_name']} rules={[{ required: true, message: 'Please input temple name!' }]}>
                <Input placeholder="Temple Name" disabled />
            </Form.Item>
            <Divider orientation="left" plain><Text strong>Daily Schedule</Text></Divider>
            <Form.List name={[templeField.name, 'schedules']}>
                {(scheduleFields) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {scheduleFields.map(scheduleField => {
                            const scheduleDateTime = form.getFieldValue(['temples_data', templeField.name, 'schedules', scheduleField.name, 'start_time']);
                            const scheduleDate = scheduleDateTime ? moment(scheduleDateTime) : null;
                            return (
                                <Card key={scheduleField.key} size="small" title={`Day ${scheduleField.name + 1} (${scheduleDate ? scheduleDate.format('MMM D, YYYY') : 'N/A'})`}>
                                    <div>
                                        <div style={{ display: 'none' }}>
                                            <Form.Item {...scheduleField} label="execution_id" name={[scheduleField.name, 'id']}>
                                                <Input />
                                            </Form.Item>
                                        </div>
                                        <Col span={12}>
                                            <Form.Item {...scheduleField} label="Puja Start Time" name={[scheduleField.name, 'start_time']} rules={[{ required: true, message: 'Please select a start time!' }]}>
                                                <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} disabledDate={(current) => !current || !scheduleDate || !current.isSame(scheduleDate, 'day')} />
                                            </Form.Item>
                                        </Col>
                                        <div>
                                            <Form.Item {...scheduleField} label="Puja Start Media" name={[scheduleField.name, 'start_media']}>
                                                <Form.List name={[scheduleField.name, 'start_media']}>
                                                    {(fields, ops) => <PujaMedia fields={fields} {...ops} form={form} cdnUrl={YODA_CDN_URL} preSignUrl={PUJA_PRESIGN_URL} datatype='Start media' onSortEnd={({ oldIndex, newIndex }) => ops.move(oldIndex, newIndex)} distance={1} helperClass='sortableHelper' />}
                                                </Form.List>
                                            </Form.Item>
                                        </div>
                                    </div>
                                </Card>
                            )
                        })}
                    </div>
                )}
            </Form.List>
        </Card>
    );
};

// Sortable Item Wrapper: Contains the Card and the Delete button.
const SortableTempleItem = SortableElement(({ field, form, onRemove, isDeletingRef }: { field: any; form: any; onRemove: () => void; isDeletingRef: React.MutableRefObject<boolean>; }) => {
    const isMultiTemple = form.getFieldValue('multi_temple');

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        console.log('Delete clicked for temple:', field.name);

        // Set deletion flag
        isDeletingRef.current = true;
        console.log('Setting deletion flag to true');

        // Direct deletion without confirmation
        onRemove();

        // Reset deletion flag after a short delay
        setTimeout(() => {
            isDeletingRef.current = false;
            console.log('Resetting deletion flag to false');
        }, 200);
    };

    return (
        <div style={{
            marginRight: '0px',
            marginBottom: '10px',
            padding: '10px 0px',
            width: '100%',
            position: 'relative',
            zIndex: 10000000,
            backgroundColor: 'white',
            cursor: 'grab'
        }} >
            <TempleCard templeField={field} form={form} />
            {/* Delete button - only show for multi-temple mode */}
            {isMultiTemple && (
                <div
                    data-no-sort
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 1000,
                        pointerEvents: 'auto'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                >
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={handleDeleteClick}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff2f0',
                            color: '#ff4d4f',
                            fontWeight: 500,
                            marginTop: '15px'
                        }}
                        title="Delete Temple"
                    />
                </div>
            )}
        </div>
    );
});

// The container for the sortable list.
const SortableTempleList = SortableContainer(({ fields, form, remove, isDeletingRef }: { fields: any[]; form: any; remove: (fieldName: number) => void; isDeletingRef: React.MutableRefObject<boolean>; }) => (
    <div>
        {fields.map((field, index) => (
            <SortableTempleItem
                key={field.key}
                index={index}
                field={field}
                form={form}
                isDeletingRef={isDeletingRef}
                onRemove={() => {
                    console.log('Removing temple at field.name:', field.name, 'actual index:', index);
                    remove(field.name); // field.name is the correct identifier for Form.List remove
                }}
            />
        ))}
    </div>
));

const PujaUpdateForm: React.FC<PujaUpdateFormProps> = ({ showForm, store, handleCloseForm }) => {
    const [pujaUpdateForm] = useForm();
    const [updateModalForm] = useForm(); // Separate form for the update modal
    const { networkInstance, countryState, app } = useAppContext();
    const monetisationPujaApi = getMonetisationPujaApi(app, networkInstance);
    const navigate = useNavigate();

    const [currentPujaState, setCurrentPujaState] = useState<string>('');
    const [isEnabled, setIsEnabled] = useState<boolean>(false);
    const [showReschedule, setShowReschedule] = useState<boolean>(false);
    const [updateConfirmDialog, setUpdateConfirmDialog] = useState<boolean>(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
    const previousTempleCountRef = useRef<number>(0);
    const isDeletingRef = useRef<boolean>(false);

    // Add state to track if temple data came from API
    const [hasApiTempleData, setHasApiTempleData] = useState<boolean>(false);

    const fetchPujaUpdates = useCallback(async (storeId: string | undefined) => {
        if (!storeId) return { err: 'Store ID is required' };
        try {
            const storeResponse = await monetisationPujaApi.storesApi.getPujaUpdatesByStoreId(storeId, countryState?.countryCode);
            return { data: storeResponse.data.data };
        } catch (err) {
            return { err: "Failed to fetch puja updates details" };
        }
    }, [countryState?.countryCode, monetisationPujaApi.storesApi]);

    const { data, error, loading } = useFetch(fetchPujaUpdates, store?.id);

    useEffect(() => {
        if (data) {
            const parsedStoreResponse = parseStoreResponse(data);
            pujaUpdateForm?.setFieldsValue(parsedStoreResponse);
            setCurrentPujaState(data?.puja_store_state || 'BOOKED');

            // Check if API returned temple data
            const apiTempleData = parsedStoreResponse?.temples_data;
            const hasApiData = apiTempleData && apiTempleData.length > 0 &&
                apiTempleData.some((temple: any) => temple.temple_id || temple.temple_name ||
                    (temple.schedules && temple.schedules.length > 0 &&
                        temple.schedules.some((schedule: any) => schedule.id || schedule.start_time)));

            setHasApiTempleData(hasApiData);
            console.log('Has API temple data:', hasApiData);

            // Initialize temple count ref after form is set
            setTimeout(() => {
                const templesData = pujaUpdateForm.getFieldValue('temples_data') || [];
                previousTempleCountRef.current = templesData.length;
                console.log('Initialized temple count ref:', templesData.length);
            }, 0);
        }
    }, [data, pujaUpdateForm]);

    const updatePujaDetails = async (values: PujaUpdateFormType) => {
        const payload = preparePayload(values);
        try {
            await monetisationPujaApi.storesApi.updatePujaStatus(store?.id, payload, countryState?.countryCode);
            handleCloseForm(true);
            message.success({ content: 'Puja Status updated Successfully', duration: 3 });
        } catch (error: any) {
            showErrorToast(error);
        }
    };

    const validateForm = () => {
        const formValues = pujaUpdateForm.getFieldsValue();
        const newPujaState = updateModalForm.getFieldValue('new_puja_state') || formValues.new_puja_state;

        // Skip validation for CANCELLED or RESCHEDULED states
        if (newPujaState === 'CANCELLED' || newPujaState === 'RESCHEDULED') {
            return true;
        }

        // Check if puja_start_time exists
        if (!formValues.puja_start_time) {
            message.error('Puja Start Date & Time is required');
            return false;
        }

        // If status is COMPLETED, check if puja_end_time exists
        if (newPujaState === 'COMPLETED' && !formValues.puja_end_time) {
            message.error('Puja End Date & Time is required when status is COMPLETED');
            return false;
        }

        return true;
    };

    const submitForm = () => {
        // If it's from the reschedule modal, handle separately
        if (showReschedule) {
            const pujaState = pujaUpdateForm.getFieldValue('new_puja_state');
            if (!pujaState) {
                message.error('Please select Puja state');
                return;
            }
            // For reschedule/cancel, only use the selected state
            const formValues = pujaUpdateForm.getFieldsValue();
            updatePujaDetails({ ...formValues, new_puja_state: pujaState });
            setShowReschedule(false);
            return;
        }

        // Validate form before submission for other cases
        if (!validateForm()) {
            return;
        }

        // If it's from the update modal, merge data from both forms
        if (updateConfirmDialog) {
            updateModalForm.validateFields()
                .then(modalValues => {
                    const mainFormValues = pujaUpdateForm.getFieldsValue();
                    const mergedValues = { ...mainFormValues, ...modalValues };
                    updatePujaDetails(mergedValues);
                })
                .catch(error => console.log('Update modal form error', error));
        } else {
            // Regular form submission
            pujaUpdateForm.validateFields()
                .then(values => updatePujaDetails(values))
                .catch(error => console.log('Puja Update form error', error));
        }
    };
    const deleteScheduledPuja = useCallback(async (storeId: string | undefined) => {
        if (!storeId) return;
        try {
            await monetisationPujaApi.storesApi.deleteScheduledPuja(storeId);
            message.success('Scheduled puja deleted successfully');
            handleCloseForm(true);
        } catch (err) {
            showErrorToast("Failed to delete scheduled puja");
        }
    }, [monetisationPujaApi.storesApi, handleCloseForm]);

    const handleRescheduleNavigation = (values: any) => {
        if (values?.new_puja_state === "RESCHEDULED") {
            navigate(`/monetisation/puja/${store?.id}/reschedule`);
        }
    };

    const handleFormValuesChange = (changedValues: any, allValues: any) => {
        // Skip all logic if currently deleting
        if (isDeletingRef.current) {
            console.log('Deletion in progress, skipping all auto-fill logic');
            return;
        }

        if (hasApiTempleData) {
            return;
        }

        const isNewFlow = allValues.multi_day || allValues.multi_temple;
        if (allValues.multi_temple && !allValues.multi_day && allValues.num_of_days !== 1) {
            pujaUpdateForm.setFieldsValue({ num_of_days: 1 });
            return;
        }
        if (!isNewFlow) return;

        const { start_date, num_of_days, temples_data } = allValues;
        const coreSettingsChanged = 'start_date' in changedValues || 'num_of_days' in changedValues || 'multi_day' in changedValues || 'multi_temple' in changedValues;
        const listChanged = 'temples_data' in changedValues;

        // Track if a temple was actually added
        let templeAdded = false;

        // If temples_data changed, check if it's addition (allow) or just nested field modification (skip)
        if (listChanged) {
            if (!coreSettingsChanged) {
                const currentCount = allValues.temples_data?.length || 0;
                const previousCount = previousTempleCountRef.current;

                console.log('Temple count changed:', { previousCount, currentCount });

                // Update the ref with current count for next comparison
                previousTempleCountRef.current = currentCount;

                if (currentCount > previousCount) {
                    console.log('Temple addition detected, proceeding with auto-fill logic');
                    templeAdded = true;
                } else if (currentCount === previousCount) {
                    console.log('Nested field change detected (not temple addition), skipping auto-fill');
                    return;
                }
            }
        }
        
        if (!start_date || !(num_of_days > 0) || !temples_data) return;
        
        let needsUpdate = false;
        if (coreSettingsChanged) {
            needsUpdate = true;
        } else if (templeAdded) {
            // Only check if schedules need updating when a temple was actually added
            needsUpdate = temples_data.some((t: any) => !t || !t.schedules || t.schedules.length !== num_of_days);
        }
        if (needsUpdate) {
            const updatedTemples = temples_data.map((temple: any = {}, templeIndex: number) => {
                const newSchedules = Array.from({ length: num_of_days }, (_, i) => {
                    const dayDate = moment(start_date).add(i, 'days');
                    const existingSchedule = temple?.schedules?.[i];
                    
                    // Always use the correct day's date, but preserve the time portion from existing schedule
                    const existingTime = existingSchedule?.start_time ? moment(existingSchedule.start_time) : null;
                    const newStartTime = dayDate.hour(existingTime?.hour() || 9).minute(existingTime?.minute() || 0).second(existingTime?.second() || 0);
                    return {
                        id: existingSchedule?.id || null,
                        day: i + 1,
                        start_time: newStartTime,
                        start_media: existingSchedule?.start_media || []
                    };
                });
                return { ...temple, schedules: newSchedules };
            });
            if (JSON.stringify(temples_data) !== JSON.stringify(updatedTemples)) {
                pujaUpdateForm.setFieldsValue({ temples_data: updatedTemples });
            }
        }
    };

    return (
        <>
            <Drawer 
                title={<DrawerTitle form={pujaUpdateForm} currentPujaState={currentPujaState} setShowDeleteConfirmModal={setShowDeleteConfirmModal} />} 
                placement='right' 
                onClose={() => { handleCloseForm(); pujaUpdateForm?.resetFields(); setHasApiTempleData(false); }} 
                visible={showForm} 
                destroyOnClose={true} 
                width={1000}
            >
                {error ? <EmptyState /> : loading ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
                    </div>
                ) : (
                    <Form 
                        form={pujaUpdateForm} 
                        layout='vertical' 
                        initialValues={{ temples_data: [{}] }} 
                        onFinish={submitForm} 
                        onFieldsChange={() => setIsEnabled(true)} 
                        onValuesChange={handleFormValuesChange}
                    >
                        <Title level={5} style={{ marginBottom: 24 }}>{store?.title}</Title>
                        <Divider orientation="left">Puja Configuration</Divider>
                        {!hasApiTempleData && (
                            <ol style={{ color: 'red', marginBottom: '10px', fontWeight: 'bold', fontSize: '15px' }}>
                                <li style={{ marginBottom: '10px' }}>⚠️ Be careful before enabling multi-day or multi-temple options.</li>
                                <li>⚠️ Once edited and updated these values cannot be edited again.</li>
                            </ol>
                        )}

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="multi_temple" label="Multi Temple" valuePropName="checked" >
                                    <Switch disabled />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="multi_day" label="Multi Day" valuePropName="checked">
                                    <Switch disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.multi_day !== curr.multi_day || prev.multi_temple !== curr.multi_temple}>
                            {({ getFieldValue }) => {
                                const isNewFlow = getFieldValue('multi_day') || getFieldValue('multi_temple');
                                const isMultiTempleOnly = getFieldValue('multi_temple') && !getFieldValue('multi_day');

                                return (
                                    <>
                                        {/* Show media section first for non-multi flows only */}
                                        {!isNewFlow && (
                                            <>
                                                <Divider orientation="left">Media, Messages & Status</Divider>
                                                <Form.Item name='puja_start_time' label='Puja start Date & Time' rules={[{ required: true, message: 'Puja Start Date & Time is required' }]}>
                                                    <DatePicker showTime style={{ width: '100%' }} />
                                                </Form.Item>
                                                <Form.Item label='Puja start media'>
                                                    <Form.List name={['puja_media']}>
                                                        {(fields, ops) => (
                                                            <PujaMedia
                                                                fields={fields}
                                                                {...ops}
                                                                form={pujaUpdateForm}
                                                                cdnUrl={YODA_CDN_URL}
                                                                preSignUrl={PUJA_PRESIGN_URL}
                                                                datatype='Puja start media'
                                                                onSortEnd={({ oldIndex, newIndex }) => ops.move(oldIndex, newIndex)}
                                                                distance={1}
                                                                helperClass='sortableHelper'
                                                            />
                                                        )}
                                                    </Form.List>
                                                </Form.Item>
                                                <Form.Item name='puja_end_time' label='Puja end Date & Time'>
                                                    <DatePicker showTime style={{ width: '100%' }} />
                                                </Form.Item>
                                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.puja_end_time !== curr.puja_end_time}>
                                                    {({ getFieldValue: fv }) => fv('puja_end_time') && (
                                                        <Form.Item label='Add Puja End Media'>
                                                            <Form.List name={['priest_medias']}>
                                                                {(fields, ops) => (
                                                                    <PujaMedia
                                                                        fields={fields}
                                                                        {...ops}
                                                                        form={pujaUpdateForm}
                                                                        cdnUrl={YODA_CDN_URL}
                                                                        preSignUrl={PUJA_PRESIGN_URL}
                                                                        datatype='Puja end Medias'
                                                                        onSortEnd={({ oldIndex, newIndex }) => ops.move(oldIndex, newIndex)}
                                                                        distance={1}
                                                                        helperClass='sortableHelper'
                                                                    />
                                                                )}
                                                            </Form.List>
                                                        </Form.Item>
                                                    )}
                                                </Form.Item>
                                            </>
                                        )}

                                        {/* Multi-day/Multi-temple specific fields */}
                                        {isNewFlow && (
                                            <>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item
                                                            name="start_date"
                                                            label="Puja Start Date"
                                                            rules={[{ required: true, message: 'Please select a start date' }]}
                                                        >
                                                            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} disabled={hasApiTempleData} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item
                                                            name="num_of_days"
                                                            label="Number of Days"
                                                            rules={[{ required: true, message: 'Please enter number of days' }]}
                                                        >
                                                            <InputNumber min={1} style={{ width: '100%' }} disabled={isMultiTempleOnly || hasApiTempleData} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item name="interval" label="Interval" initialValue="day">
                                                            <Input disabled={true} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Divider orientation="left">Temple Schedules</Divider>
                                                <Form.List name="temples_data">
                                                    {(fields, { add, remove, move }) => {
                                                        // For multi-day with single temple, ensure we have at least one temple
                                                        if (!getFieldValue('multi_temple') && fields.length === 0) {
                                                            add({ schedules: [] });
                                                        }

                                                        return getFieldValue('multi_temple') ? (
                                                            <>
                                                                <SortableTempleList
                                                                    fields={fields}
                                                                    form={pujaUpdateForm}
                                                                    isDeletingRef={isDeletingRef}
                                                                    remove={(fieldName) => {
                                                                        console.log('Form.List remove called with fieldName:', fieldName);
                                                                        remove(fieldName);
                                                                    }}
                                                                    onSortEnd={({ oldIndex, newIndex }) => {
                                                                        console.log('Sort end:', oldIndex, 'to', newIndex);
                                                                        move(oldIndex, newIndex);
                                                                    }}
                                                                    useDragHandle
                                                                    shouldCancelStart={(e) => {
                                                                        // Prevent sorting when clicking delete button or its container
                                                                        const target = e.target as Element;
                                                                        if (target.closest('[data-no-sort]')) {
                                                                            console.log('Cancelled sort start due to no-sort element');
                                                                            return true;
                                                                        }
                                                                        return false;
                                                                    }}
                                                                />
                                                                <Form.Item>
                                                                    <Button
                                                                        type="dashed"
                                                                        onClick={() => add({ schedules: [] })}
                                                                        block
                                                                        icon={<PlusOutlined />}
                                                                    >
                                                                        Add Temple
                                                                    </Button>
                                                                </Form.Item>
                                                            </>
                                                        ) : (
                                                            // Single temple for multi-day
                                                            fields.length > 0 ? <TempleCard templeField={fields[0]} form={pujaUpdateForm} /> : null
                                                        );
                                                    }}
                                                </Form.List>
                                            </>
                                        )}

                                        {/* Media section appears after temple schedules for multi flows */}
                                        {isNewFlow && (
                                            <>
                                                <Divider orientation="left">Media, Messages & Status</Divider>
                                                <Form.Item name='puja_start_time' label='Puja start Date & Time' rules={[{ required: true, message: 'Puja Start Date & Time is required' }]}>
                                                    <DatePicker showTime style={{ width: '100%' }} />
                                                </Form.Item>
                                                <Form.Item label='Puja start media'>
                                                    <Form.List name={['puja_media']}>
                                                        {(fields, ops) => (
                                                            <PujaMedia
                                                                fields={fields}
                                                                {...ops}
                                                                form={pujaUpdateForm}
                                                                cdnUrl={YODA_CDN_URL}
                                                                preSignUrl={PUJA_PRESIGN_URL}
                                                                datatype='Puja start media'
                                                                onSortEnd={({ oldIndex, newIndex }) => ops.move(oldIndex, newIndex)}
                                                                distance={1}
                                                                helperClass='sortableHelper'
                                                            />
                                                        )}
                                                    </Form.List>
                                                </Form.Item>
                                                <Form.Item name='puja_end_time' label='Puja end Date & Time'>
                                                    <DatePicker showTime style={{ width: '100%' }} />
                                                </Form.Item>
                                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.puja_end_time !== curr.puja_end_time}>
                                                    {({ getFieldValue: fv }) => fv('puja_end_time') && (
                                                        <Form.Item label='Add Puja End Media'>
                                                            <Form.List name={['priest_medias']}>
                                                                {(fields, ops) => (
                                                                    <PujaMedia
                                                                        fields={fields}
                                                                        {...ops}
                                                                        form={pujaUpdateForm}
                                                                        cdnUrl={YODA_CDN_URL}
                                                                        preSignUrl={PUJA_PRESIGN_URL}
                                                                        datatype='Puja end Medias'
                                                                        onSortEnd={({ oldIndex, newIndex }) => ops.move(oldIndex, newIndex)}
                                                                        distance={1}
                                                                        helperClass='sortableHelper'
                                                                    />
                                                                )}
                                                            </Form.List>
                                                        </Form.Item>
                                                    )}
                                                </Form.Item>
                                            </>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                        <Divider />
                        <Form.Item label='Message cards'>
                            <Form.List name={['message_cards']}>
                                {(fields, ops) => (
                                    <MessageCard 
                                        fields={fields} 
                                        {...ops} 
                                        form={pujaUpdateForm} 
                                        cdnUrl={YODA_CDN_URL} 
                                        preSignUrl={PUJA_PRESIGN_URL} 
                                        datatype='Message card' 
                                        onSortEnd={({ oldIndex, newIndex }) => ops.move(oldIndex, newIndex)} 
                                        distance={1} 
                                        helperClass='sortableHelper' 
                                    />
                                )}
                            </Form.List>
                        </Form.Item>
                        <Row justify="space-between" style={{ width: '100%', marginTop: '24px' }}>
                            <Button type="primary" style={{ flexBasis: '48%' }} disabled={!isEnabled} onClick={() => setUpdateConfirmDialog(true)}>Update</Button>
                            <Button danger style={{ flexBasis: '48%' }} onClick={() => setShowReschedule(true)}>Reschedule / Cancel</Button>
                        </Row>
                    </Form>
                )}
            </Drawer>
            
            {/* Reschedule Modal */}
            <Modal 
                title="Puja Reschedule / Cancel" 
                visible={showReschedule} 
                onOk={submitForm} 
                onCancel={() => setShowReschedule(false)} 
                width="35vw" 
                okText='Update'
            >
                <Form 
                    form={pujaUpdateForm}
                    onValuesChange={(values) => {
                        if (values?.new_puja_state === "RESCHEDULED") {
                            navigate(`/monetisation/puja/${store?.id}/reschedule`);
                        }
                    }}
                >
                    <Form.Item 
                        label="Puja state" 
                        name={['new_puja_state']} 
                        rules={[{ required: true, message: 'Please select Puja state' }]}
                    >
                        <Select placeholder='Select Puja state'>
                            <Option value='RESCHEDULED'>RESCHEDULED</Option>
                            <Option value='CANCELLED'>CANCELLED</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Update Modal */}
            <Modal 
                title="Puja update" 
                visible={updateConfirmDialog} 
                onOk={submitForm} 
                onCancel={() => setUpdateConfirmDialog(false)} 
                width="35vw" 
                okText='Update'
            >
                <Form form={updateModalForm} layout="vertical">
                    <Form.Item label="Puja state" name={['new_puja_state']}>
                        <Select defaultValue={'BOOKED'} placeholder='Select Puja state'>
                            <Option value='BOOKED'>BOOKED</Option>
                            <Option value='SCHEDULED'>SCHEDULED</Option>
                            <Option value='STARTED'>STARTED</Option>
                            <Option value='COMPLETED'>COMPLETED</Option>
                        </Select>
                    </Form.Item>
                    {/* Conditional rendering for status update fields */}
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.multi_day !== curr.multi_day || prev.multi_temple !== curr.multi_temple}>
                        {({ getFieldValue, setFieldValue }) => {
                            const isMultiFlow = getFieldValue('multi_day') || getFieldValue('multi_temple');
                            return !isMultiFlow ? (
                                <>
                                    <Form.Item name="change_status_immediately" valuePropName="checked">
                                        <Checkbox disabled={getFieldValue('is_scheduled') === true}>Change the status immediately</Checkbox>
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate={(p, c) => p.change_status_immediately !== c.change_status_immediately}>
                                        {({ getFieldValue: fv }) => (
                                            <Form.Item
                                                name='init_scheduled_time'
                                                label='Scheduled update'
                                                rules={[{ required: !fv('change_status_immediately'), message: 'Pick time' }]}
                                            >
                                                <DatePicker showTime disabled={fv('change_status_immediately')} style={{ width: '100%' }} />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </>
                            ) : null;
                        }}
                    </Form.Item>
                </Form>
            </Modal>

            <Modal 
                title="Confirm Delete" 
                visible={showDeleteConfirmModal} 
                onOk={() => { deleteScheduledPuja(store?.id); setShowDeleteConfirmModal(false); }} 
                onCancel={() => setShowDeleteConfirmModal(false)} 
                okText="Yes, Delete" 
                cancelText="Cancel" 
                width="30vw"
            >
                <p>Are you sure you want to delete the scheduled puja?</p>
            </Modal>
        </>
    );
}

const DrawerTitle = ({ form, currentPujaState, setShowDeleteConfirmModal }: { form: any, currentPujaState: string, setShowDeleteConfirmModal: (show: boolean) => void }) => {
    const scheduledStates = form.getFieldValue('scheduled_states') || [];
    return (
        <Row justify="space-between" align="middle" style={{ width: '100%' }}>
            <span>Puja Update Form</span>
            {scheduledStates.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button danger type="primary" size="small" onClick={() => setShowDeleteConfirmModal(true)}>Delete</Button>
                    {scheduledStates.map((state: ScheduledStates, index: number) => (
                        <Fragment key={index}>
                            <div style={{ textAlign: 'center' }}>
                                <Tag color="blue">{state.state}</Tag>
                                <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px' }}>{state.scheduled_at ? moment(state.scheduled_at).format('DD-MMM-YY HH:mm') : 'Current'}</div>
                            </div>
                            {index < scheduledStates.length - 1 && <RightOutlined />}
                        </Fragment>
                    ))}
                </div>
            ) : <Tag color="blue">{currentPujaState}</Tag>}
        </Row>
    );
};

export default memo(PujaUpdateForm);